/**
 * stripeService
 *
 * Client utilities for Stripe: create/verify checkout sessions, redirect to
 * Checkout or Billing Portal, and lazy-load Stripe.js. Includes resilience
 * and fallbacks to preserve auth/session through redirects.
 */
import { post, SERVER_URL } from '../api/core';
// Removed auth import as idToken is passed into functions needing it

// Get the Stripe public key from environment variables (legacy only)
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

// Runtime override for selecting which public key to use
function setStripePublicKeyOverride(key) {
  try {
    if (key && typeof key === 'string') {
      window.__STRIPE_PK_OVERRIDE = key;
    } else {
      delete window.__STRIPE_PK_OVERRIDE;
    }
  } catch (_) {}
}

function getStripePublicKey() {
  return (typeof window !== 'undefined' && window.__STRIPE_PK_OVERRIDE)
    ? window.__STRIPE_PK_OVERRIDE
    : STRIPE_PUBLIC_KEY;
}

export function setStripePublicKeyForUser(_user) {
  // Always use legacy public key
  setStripePublicKeyOverride(STRIPE_PUBLIC_KEY);
  console.log('Stripe Public Key determined. Using legacy key.');
}

/**
 * Creates a Stripe checkout session for the specified plan level and interval
 * @param {string} planLevel - The level of the plan ('premium')
 * @param {string} interval - The billing interval ('monthly' or 'annual')
 * @param {string} theme - The theme mode ('light' or 'dark'), defaults to 'light'
 * @param {boolean} isNewSignup - Whether this is being called right after signup
 * @returns {Promise<{sessionId: string}>} - The Stripe checkout session ID
 */
export const createCheckoutSession = async (planLevel, interval, theme = 'light', isNewSignup = false) => {
  try {
    console.log(`Creating Stripe checkout session for plan: ${planLevel}, interval: ${interval}, theme: ${theme}, isNewSignup: ${isNewSignup}`);

    if (!planLevel || !interval) {
      throw new Error('Plan level and interval are required.');
    }

    // Get the current auth user
    const auth = (await import('../firebase/firebase')).auth;
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error('No authenticated user found when creating checkout session');
      throw new Error('You must be logged in to create a checkout session.');
    }
    
    // Ensure we have a fresh token before proceeding
    try {
      await currentUser.getIdToken(true);
      console.log('Token refreshed for Stripe checkout session');
    } catch (tokenError) {
      console.error('Error refreshing token:', tokenError);
      throw new Error('Authentication error. Please refresh the page and try again.');
    }
    
    console.log('Creating checkout with user ID:', currentUser.uid);

    // Make the request to create the checkout session
    const response = await post('/stripe/create-checkout-session', {
      planLevel: planLevel.toLowerCase(),
      interval: interval.toLowerCase(),
      theme: theme,
      isNewSignup: isNewSignup || localStorage.getItem('just_signed_up') === 'true'
    });

    // Validate the session ID from the response
    if (!response || !response.sessionId) {
      console.error('Invalid response from create-checkout-session:', response);
      throw new Error('No valid session ID returned from server');
    }

    // Log the session ID for debugging
    console.log(`Successfully created Stripe checkout session: ${response.sessionId}`);

    // Store minimal session info for potential retry
    localStorage.setItem('stripeSessionId', response.sessionId);
    localStorage.setItem('stripeSessionCreatedAt', Date.now().toString());

    return response; // Contains { sessionId: '...' }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    
    // Special handling for "use billing portal" error code
    if (error.response?.data?.code === 'USE_BILLING_PORTAL') {
      throw new Error('Please use the billing portal to manage your existing subscription');
    }
    
    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create checkout session.';
    const errorDetails = error?.response?.data?.details;
    console.error('Details:', errorDetails);
    throw new Error(`${errorMessage}${errorDetails ? ` (${errorDetails})` : ''}`);
  }
};

/**
 * Verifies the subscription status after redirect back from Stripe Checkout.
 * @param {string} sessionId - The Stripe Checkout Session ID from the success URL query params.
 * @returns {Promise<object>} - The backend response, likely including user profile update.
 */
export const verifySubscription = async (sessionId) => {
  // Validate session ID
  if (!sessionId) {
    throw new Error('No session ID provided for verification');
  }
  
  console.log(`[StripeService] Starting verification for session: ${sessionId}`);
  
  // Get stored plan information - the server requires these parameters
  let planInterval = null;
  let planLevel = null;
  
  try {
    // Try to get from selectedPlan first (most recent)
    const selectedPlanData = localStorage.getItem('selectedPlan');
    if (selectedPlanData) {
      const planInfo = JSON.parse(selectedPlanData);
      planInterval = planInfo.frequency || planInfo.interval; // Handle both formats
      planLevel = planInfo.planId || planInfo.planLevel; // Handle both formats
      console.log('[StripeService] Retrieved plan info from selectedPlan:', { planLevel, planInterval });
    }
    
    // If not found, try to get from checkout session metadata
    if (!planInterval || !planLevel) {
      const checkoutState = localStorage.getItem('stripe_checkout_auth_state');
      if (checkoutState) {
        const state = JSON.parse(checkoutState);
        if (state.planLevel) planLevel = state.planLevel;
        if (state.planInterval) planInterval = state.planInterval;
        console.log('[StripeService] Retrieved plan info from checkout state:', { planLevel, planInterval });
      }
    }
    
    // If still not found, try to extract from URL or other sources
    if (!planInterval || !planLevel) {
      // Check if there's plan info in the URL params
      const urlParams = new URLSearchParams(window.location.search);
      const urlPlanLevel = urlParams.get('plan_level') || urlParams.get('planLevel');
      const urlPlanInterval = urlParams.get('plan_interval') || urlParams.get('planInterval');
      
      if (urlPlanLevel) planLevel = urlPlanLevel;
      if (urlPlanInterval) planInterval = urlPlanInterval;
      
      if (urlPlanLevel || urlPlanInterval) {
        console.log('[StripeService] Retrieved plan info from URL params:', { planLevel, planInterval });
      }
    }
    
  } catch (e) {
    console.warn('[StripeService] Could not retrieve stored plan information:', e);
  }
  
  // Prepare the verification payload - server expects these exact parameter names
  const verificationPayload = { 
    sessionId,
    // Include plan information if available (server expects these)
    ...(planInterval && { planInterval }),
    ...(planLevel && { planLevel })
  };
  
  console.log('[StripeService] Verification payload:', verificationPayload);
  
  try {
    // Make the verification request
    const response = await post('/stripe/verify-payment', verificationPayload);
    console.log('[StripeService] Verification response:', response);
      
      // Clean up stored session data after successful verification
      localStorage.removeItem('stripeSessionId');
      localStorage.removeItem('stripeSessionCreatedAt');
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('stripe_checkout_auth_state');
      
      return response;
    
    } catch (error) {
    console.error('[StripeService] Verification error:', error);
      
    // Provide more specific error messages based on the error type
    let errorMessage = 'Failed to verify subscription.';
    
    if (error?.response?.status === 400) {
      const errorData = error.response.data;
      if (errorData?.error?.includes('required')) {
        errorMessage = 'Missing required payment information. Please try again or contact support.';
      } else {
        errorMessage = errorData?.error || 'Invalid payment session. Please contact support.';
      }
    } else if (error?.response?.status === 403) {
      errorMessage = 'Payment session verification failed. Please contact support.';
    } else if (error?.response?.status === 500) {
      errorMessage = 'Server error during verification. Your payment was likely successful - please check your account.';
    } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      errorMessage = 'Network error during verification. Please check your connection and try again.';
    } else if (error?.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error?.message) {
      errorMessage = error.message;
    }
      
    // Preserve session data for potential manual retry
      if (sessionId) {
        try {
          const sessionData = {
            sessionId,
          planInterval,
          planLevel,
            timestamp: new Date().toISOString(),
            lastError: errorMessage
          };
          localStorage.setItem('failedVerificationSession', JSON.stringify(sessionData));
        console.log('[StripeService] Saved failed verification data for potential manual retry');
        } catch (e) {
        console.error('[StripeService] Error saving failed verification data:', e);
        }
      }
      
      throw new Error(errorMessage);
    }
};

/**
 * Requests a billing portal session and redirects the user to it.
 * @param {string} returnUrl - The URL to return to after the portal session.
 * @returns {Promise<object>} - The response from the server.
 */
export const redirectToBillingPortal = async (returnUrl) => {
  try {
    // Get the user's ID token to ensure fresh authentication
    const auth = (await import('../firebase/firebase')).auth;
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('No authenticated user found. Please log in again.');
    }
    
    console.log('Requesting Stripe billing portal session for user:', currentUser.uid);
    console.log('Return URL:', returnUrl);
    
    // Set a flag in localStorage to indicate we're going to Stripe portal
    localStorage.setItem('stripePortalRedirect', JSON.stringify({
      timestamp: Date.now(),
      returnUrl: returnUrl || window.location.href
    }));
    
    const response = await post('/stripe/create-billing-portal', { 
      returnUrl: returnUrl || window.location.href
    });
    
    if (!response || !response.portalUrl) {
      throw new Error('No portal URL returned from server');
    }
    
    console.log('Redirecting to Stripe billing portal:', response.portalUrl);
    
    // Start the redirect
    window.location.href = response.portalUrl;
    
    // Return a promise that doesn't resolve immediately
    // This gives the browser time to start navigation before the function returns
    return new Promise((resolve) => {
      // Set a short timeout to delay resolution
      // This makes the function keep "running" for a bit to prevent premature error handling
      setTimeout(() => {
        // If we're still here after 500ms, it means the redirect might be happening
        // but the page hasn't unloaded yet. Just resolve with the response.
        resolve(response);
      }, 500);
    });
  } catch (error) {
    console.error('Error redirecting to billing portal:', error);
    const errorMessage = error?.response?.data?.error || error?.message || 'Failed to open billing portal.';
    const errorDetails = error?.response?.data?.details;
    throw new Error(`${errorMessage}${errorDetails ? ` (${errorDetails})` : ''}`);
  }
};

/**
 * Loads the Stripe.js script and initializes Stripe
 * @param {string} publicKey - The Stripe public key
 * @returns {Promise<Stripe>} - The Stripe instance
 */
const loadStripe = (function() {
  let stripePromise = null;

  return function(publicKey) {
    if (!stripePromise) {
      stripePromise = new Promise((resolve, reject) => {
        // Check if Stripe is already loaded
        if (window.Stripe) {
          console.log('Stripe already loaded, initializing with public key');
          try {
              const stripeInstance = window.Stripe(publicKey);
              resolve(stripeInstance);
          } catch (e) {
               console.error('Error initializing Stripe:', e);
               reject(new Error('Failed to initialize Stripe with the provided key.'));
          }
          return;
        }

        // Create script element
        console.log('Loading Stripe.js script');
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;

        // Add both success and error handlers
        script.onload = () => {
          console.log('Stripe.js script loaded successfully');
          if (window.Stripe) {
             try {
                const stripeInstance = window.Stripe(publicKey);
                resolve(stripeInstance);
             } catch (e) {
                console.error('Error initializing Stripe after load:', e);
                reject(new Error('Stripe loaded but failed to initialize. Check your public key.'));
             }
          } else {
            console.error('Stripe.js loaded but window.Stripe is not defined');
            reject(new Error('Stripe.js loaded but window.Stripe is not available'));
          }
        };

        script.onerror = (event) => {
          console.error('Failed to load Stripe.js script', event);
          reject(new Error('Failed to load Stripe.js script. Check network connection and script URL.'));
        };

        // Append the script to the document head or body
        document.head.appendChild(script);
      });
    }
    return stripePromise;
  };
})();

/**
 * Redirects to Stripe Checkout with the given session ID
 * @param {string} sessionId - The Stripe checkout session ID
 */
export const redirectToCheckout = async (sessionId) => {
  try {
    console.log('Starting redirect to Stripe checkout with session ID:', sessionId);

    if (!getStripePublicKey()) {
      console.error('Stripe public key (VITE_STRIPE_PUBLIC_KEY) is not set in environment variables.');
      throw new Error('Stripe configuration is missing.');
    }
    if (!sessionId) {
      console.error('redirectToCheckout called without a sessionId.');
      throw new Error('Cannot redirect to checkout without a session ID.');
    }

    // IMPORTANT: Ensure Firebase auth persistence is properly set before redirecting
    try {
      const { setPersistence, browserLocalPersistence, browserSessionPersistence } = await import('firebase/auth');
      const { auth } = await import('../firebase/firebase');
      
      // Make sure we have the strongest persistence to survive redirects
      await setPersistence(auth, browserLocalPersistence);
      console.log('Firebase auth persistence set to LOCAL for Stripe redirect');
      
      // CRITICAL FIX: Save auth credentials in localStorage to enable auto-login when returning
      if (auth.currentUser) {
        // First get a fresh token
        await auth.currentUser.getIdToken(true);
        console.log('Firebase token refreshed before Stripe redirect');
        
        // Get current user's email which is needed for auto-login
        const userEmail = auth.currentUser.email;
        
        // Save last authenticated email in localStorage
        // This will be used for auto-login if Firebase auth is lost
        if (userEmail) {
          localStorage.setItem('stripe_auth_email', userEmail);
          localStorage.setItem('stripe_auth_uid', auth.currentUser.uid);
          localStorage.setItem('stripe_auth_displayName', auth.currentUser.displayName || '');
          localStorage.setItem('stripe_auth_timestamp', Date.now().toString());
          localStorage.setItem('stripe_auto_login_needed', 'true');
          localStorage.setItem('stripe_session_id', sessionId);
          
          console.log('Saved auth credentials for auto-login after Stripe redirect');
        }
        
        // Also store in sessionStorage for same-tab persistence
        sessionStorage.setItem('stripe_auth_email', userEmail);
        sessionStorage.setItem('stripe_auth_uid', auth.currentUser.uid);
        
        // CRITICAL: Also save auth state before redirect
        const authState = {
          uid: auth.currentUser.uid,
          email: userEmail,
          timestamp: Date.now(),
          sessionId: sessionId,
          redirectPage: window.location.pathname
        };
        localStorage.setItem('stripe_checkout_auth_state', JSON.stringify(authState));
      } else {
        console.warn('No authenticated user found before Stripe redirect - this may cause issues');
      }
    } catch (authError) {
      console.warn('Error ensuring auth persistence before redirect:', authError);
      // Continue anyway - this is a precaution not a blocker
    }

    // Store minimal session info for potential recovery if redirect fails
    localStorage.setItem('stripeSessionId', sessionId);
    localStorage.setItem('stripeSessionCreatedAt', Date.now().toString());
    
    // Store a flag to identify we're in the Stripe checkout flow
    sessionStorage.setItem('in_stripe_checkout_flow', 'true');
    localStorage.setItem('stripe_checkout_initiated', 'true');
    
    // Store the last page we were on before redirect to return here if needed
    localStorage.setItem('stripe_checkout_return_path', window.location.pathname);
    
    // Detect mobile device for logging purposes
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      console.log('Mobile device detected, using Stripe.js redirect (same as desktop)');
    }
    
    // Use the official Stripe.js redirect for ALL devices (mobile and desktop)
    console.log('Initializing Stripe...');
    const stripe = await loadStripe(getStripePublicKey());

    console.log('Attempting redirect using stripe.redirectToCheckout...');
    const { error } = await stripe.redirectToCheckout({ sessionId: sessionId });

    if (error) {
      console.error('Stripe redirectToCheckout error:', error);
      
      // Try direct URL as fallback if Stripe.js redirect fails
      if (error.type === 'validation_error' || error.message.includes('invalid')) {
        throw new Error(`Checkout validation error: ${error.message}`);
      }
      
      console.warn('Stripe.js redirect failed, trying direct URL as fallback:', error.message);
      
      // FIXED: Use the correct Stripe checkout URL format for fallback
      // Use the session.url from the checkout session if available, or try multiple URL patterns
      const fallbackUrls = [
        `https://checkout.stripe.com/c/pay/${sessionId}`,  // Standard format
        `https://checkout.stripe.com/pay/${sessionId}#fidkdWxOYHwnPyd1blpxblppbHNgWjA0VEZpNmp8VjZiMk1AaDI0NWtpd1BRcjVkQVNONFN8X0twdXJyXzJgNnFCY2tWfCcpJ2N3amhWYHdzYGlrZGlrZGZgc3FgJz9xd3BgeCUl`,  // Alternate format
      ];
      
      // Try the first fallback URL
      const fallbackUrl = fallbackUrls[0];
      console.log('Attempting direct redirect to:', fallbackUrl);
      window.location.href = fallbackUrl;
      
      // If we're still here after 3 seconds, try the second fallback URL
      setTimeout(() => {
        if (document.visibilityState !== 'hidden') {
          console.warn('First fallback did not navigate away, trying second fallback URL');
          window.location.href = fallbackUrls[1];
        }
      }, 3000);
      
      return;
    }
    
    // If we're still here after 5 seconds, something might be wrong
    setTimeout(() => {
      if (document.visibilityState !== 'hidden') {
        console.warn('Stripe redirect did not navigate away after 5 seconds, trying final fallback');
        const finalFallbackUrl = `https://checkout.stripe.com/c/pay/${sessionId}`;
        window.location.href = finalFallbackUrl;
      }
    }, 5000);

  } catch (error) {
    console.error('Error during redirect process:', error);
    throw error;
  }
};

// Optional: Helper to initialize Stripe early if needed
export const initializeStripe = (publicKey) => {
    const keyToUse = publicKey || getStripePublicKey();
    if (!keyToUse) {
      console.warn('Stripe public key is not set. Cannot initialize Stripe.');
      return Promise.reject('Missing Stripe public key');
    }
    return loadStripe(keyToUse);
};

// Optional: Connection check (remains the same)
export const checkStripeConnection = async () => {
  try {
    console.log('Checking Stripe.js reachability...');
    // Using fetch with HEAD is a lightweight way to check if the domain is reachable
    await fetch('https://js.stripe.com/v3/', { method: 'HEAD', mode: 'no-cors' });
    console.log('Stripe.js seems reachable.');
    return true;
  } catch (error) {
    // This will likely fail due to CORS in `no-cors` mode, but reaching the catch
    // block after the fetch attempt suggests network failure or DNS issues.
    // A successful `no-cors` HEAD request usually returns an opaque response, not an error.
    // The error here might indicate a network problem preventing reaching Stripe.
    console.error('Stripe.js reachability check failed (might indicate network issue):', error);
    return false;
  }
};

/**
 * Initiates a checkout session for a new subscription
 * Uses the new AuthContext checkout flow to maintain session state
 * 
 * @param {string} planLevel - The plan level to subscribe to (currently only 'premium')
 * @param {string} interval - The billing interval ('month' or 'year')
 * @param {object} options - Additional options
 * @returns {Promise<object>} - The checkout result
 */
export const initiateCheckout = async (planLevel, interval, options = {}) => {
  // Get auth context to access checkout functions
  const auth = await import('../context/auth').then(module => module.useAuth());
  const { initiateStripeCheckout } = auth;
  
  try {
    // Get Firebase auth token
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('User must be logged in to initiate checkout');
    }
    
    const token = await currentUser.getIdToken();
    
    // Prepare checkout request data
    const requestData = {
      planLevel,
      interval,
      ...options
    };
    
    // Create checkout session with the server
    const response = await fetch(`${SERVER_URL}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }
    
    const data = await response.json();
    
    if (!data.sessionId) {
      throw new Error('No session ID returned from server');
    }
    
    // Use the new checkout flow to store session state
    if (initiateStripeCheckout) {
      initiateStripeCheckout(data.sessionId);
    } else {
      // Fallback if context is not available
      localStorage.setItem('stripe_session_id', data.sessionId);
      localStorage.setItem('stripe_checkout_initiated', 'true');
      localStorage.setItem('stripe_checkout_auth_state', JSON.stringify({
        uid: currentUser.uid,
        timestamp: Date.now()
      }));
    }
    
    return {
      success: true,
      sessionId: data.sessionId,
      url: `https://checkout.stripe.com/pay/${data.sessionId}`,
      hasTrial: data.hasTrial
    };
  } catch (error) {
    console.error('Error initiating checkout:', error);
    return {
      success: false,
      error: error.message || 'Failed to initiate checkout'
    };
  }
};

/**
 * Verifies a successful payment after Stripe checkout
 * 
 * @param {string} sessionId - The Stripe checkout session ID
 * @returns {Promise<object>} - The verification result
 */
export const verifyCheckoutPayment = async (sessionId) => {
  try {
    // Get Firebase auth token
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      throw new Error('User must be logged in to verify payment');
    }
    
    const token = await currentUser.getIdToken(true); // Force refresh token
    
    // Verify payment with the server
    const response = await fetch(`${SERVER_URL}/stripe/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ sessionId })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Payment verification failed');
    }
    
    const paymentData = await response.json();
    
    // Clean up stored checkout state
    localStorage.removeItem('stripe_session_id');
    localStorage.removeItem('stripe_checkout_initiated');
    localStorage.removeItem('stripe_checkout_auth_state');
    
    return {
      success: true,
      ...paymentData
    };
  } catch (error) {
    console.error('Error verifying payment:', error);
    return {
      success: false,
      error: error.message || 'Failed to verify payment'
    };
  }
};